import React, { useState} from 'react';
import { useNavigate, Link} from 'react-router-dom';
import { useDispatch, useSelector} from 'react-redux';
import { registerUser} from '../redux/authSlice';
import { MessageCircle, Mail, Lock, Building2, Phone, Loader, AlertCircle, Eye, EyeOff} from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import PhoneInput from '../components/PhoneInput';
import Seo from '../components/Seo';

function RegisterPage() {
 const navigate = useNavigate();
 const dispatch = useDispatch();
 const { loading, error} = useSelector(state => state.auth);

 const [formData, setFormData] = useState({
 companyName: '',
 email: '',
 password: '',
 confirmPassword: '',
 phoneNumber: '',
});

 const [validationErrors, setValidationErrors] = useState({});
 const [showPassword, setShowPassword] = useState(false);
 const [showConfirmPassword, setShowConfirmPassword] = useState(false);

 const validate = () => {
 const errors = {};
 if (!formData.companyName.trim()) errors.companyName = 'Company name is required';
 if (!formData.email) errors.email = 'Email required';
 else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Invalid format';
 if (!formData.password) errors.password = 'Password required';
 else if (formData.password.length < 8 || !/[A-Z]/.test(formData.password) || !/[a-z]/.test(formData.password) || !/[0-9]/.test(formData.password) || !/[^A-Za-z0-9]/.test(formData.password)) errors.password = '8+ characters with upper and lower case, a number and a symbol';
 if (formData.password !== formData.confirmPassword) errors.confirmPassword = 'Mismatch';
 setValidationErrors(errors);
 return Object.keys(errors).length === 0;
};

 const handleChange = (e) => {
 const { name, value} = e.target;
 setFormData(prev => ({ ...prev, [name]: value}));
 if (validationErrors[name]) setValidationErrors(prev => ({ ...prev, [name]: ''}));
};

 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!validate()) return;
 const result = await dispatch(registerUser(formData));
 if (result.meta.requestStatus === 'fulfilled') {
 navigate('/dashboard/subscription');
}
};

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-[#F3F3F3] px-4 py-8">
      <Seo
        title="Start Free Trial | WaapLeads CRM"
        description="Create your WaapLeads CRM account to launch AI CRM workflows, WhatsApp automation, Meta Ads management, and customer engagement operations."
        pathname="/register"
      />
      {/* ── RIGHT SIDE: REGISTER FORM ── */}
      <div className="w-full max-w-md bg-white border border-[#E5E5E5] rounded-lg p-6 sm:p-8 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="w-full">
          <div className="flex flex-col items-center mb-6 text-center">
            <BrandLogo size={40} showText={false} className="mb-4" />
            <h1 className="text-xl font-bold text-[#2E2E2E] mb-1">Create Account</h1>
            <p className="text-[13px] text-[#5C5C5C] font-medium">Create your workspace account</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-[#FEF1EE] border border-[#FDDDE3] rounded flex items-center gap-3 text-[#BA0517] text-[13px] font-semibold">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2 slds-input-group">
              <label htmlFor="companyName" className="text-[11px] font-semibold text-[#706E6B]">Company Name</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={16} />
                <input
                  id="companyName"
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  className={validationErrors.companyName ? "pl-10 w-full h-9 border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none border-[#BA0517]" : "pl-10 w-full h-9 border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none"}
                  placeholder="e.g. Acme Corp"
                />
              </div>
              {validationErrors.companyName && <p className="text-[#BA0517] text-[11px] font-medium mt-1 ml-1">{validationErrors.companyName}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2 slds-input-group">
                <label htmlFor="email" className="text-[11px] font-semibold text-[#706E6B]">Work Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={16} />
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={validationErrors.email ? "pl-10 w-full h-9 border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none border-[#BA0517]" : "pl-10 w-full h-9 border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none"}
                    placeholder="name@company.com"
                  />
                </div>
              </div>
              <div className="space-y-2 slds-input-group">
                <label className="text-[11px] font-semibold text-[#706E6B]">Phone</label>
                <PhoneInput
                  value={formData.phoneNumber}
                  onChange={val => setFormData(prev => ({ ...prev, phoneNumber: val}))}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2 slds-input-group">
                <label htmlFor="password" className="text-[11px] font-semibold text-[#706E6B]">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={16} />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={validationErrors.password ? "pl-10 pr-12 w-full h-9 border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none border-[#BA0517]" : "pl-10 pr-12 w-full h-9 border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none"}
                    placeholder="Min 6 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-3 flex items-center text-[#706E6B] hover:text-[#2E2E2E] transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="space-y-2 slds-input-group">
                <label htmlFor="confirmPassword" className="text-[11px] font-semibold text-[#706E6B]">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={16} />
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={validationErrors.confirmPassword ? "pl-10 pr-12 w-full h-9 border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none border-[#BA0517]" : "pl-10 pr-12 w-full h-9 border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none"}
                    placeholder="Repeat key"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(v => !v)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-3 flex items-center text-[#706E6B] hover:text-[#2E2E2E] transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            <button 
              type="submit" 
              className="w-full h-9 bg-[#0176D3] hover:bg-[#014486] text-white rounded text-[13px] font-semibold disabled:opacity-50"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-3">
                  <Loader size={18} className="animate-spin" /> 
                  <span className="animate-pulse">Creating Account…</span>
                </span>
              ) : (
                "Establish Corporation Access"
              )}
            </button>
          </form>

          <div className="mt-6 text-center pt-4 border-t border-[#E5E5E5]">
            <p className="text-[13px] text-[#5C5C5C] font-medium">
              Already registered? <Link to="/login" className="text-[var(--slds-color-brand)] font-bold hover:underline ml-1">Back to Login</Link>
            </p>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-[11px] text-[#706E6B] font-bold leading-relaxed">
              By signing up, you agree to our <span className="text-[#2E2E2E]">Terms of Service</span> & <span className="text-[#2E2E2E]">Privacy Policy</span>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
